import type { History, OutputState, Q, Tone } from './types';

export interface WavePaths { good: string; warn: string; bad: string; idle: string; target: string; max: number }

/**
 * Oscilloscope paths: height = bandwidth, colour = quality of each 5 s window.
 * Each segment takes the class of its newer point, so a dip turns the line
 * amber/red where it happens. The scale fits the data (and the target, if any).
 */
export function wavePaths(h: History, w: number, ht: number, targetMbps?: number): WavePaths {
  const n = Math.max(h.length, 2);
  const top = Math.max(1, targetMbps ?? 0, ...h.map((p) => p.v)) * 1.15;
  const y = (v: number) => (v < 0 ? ht - 3 : ht - 3 - (Math.min(v, top) / top) * (ht - 9));
  // Fewer than 60 points: draw them at the right edge (now), the rest is empty.
  const off = 60 - n;
  const x = (i: number) => ((i + Math.max(0, off)) * w) / 59;
  const p: Record<Q, string> = { good: '', warn: '', bad: '', idle: '' };
  for (let i = 1; i < h.length; i++) {
    const a = h[i - 1]!, b = h[i]!;
    p[b.q] += `M${x(i - 1).toFixed(1)} ${y(a.v).toFixed(1)}L${x(i).toFixed(1)} ${y(b.v).toFixed(1)}`;
  }
  return { ...p, target: targetMbps ? `M0 ${y(targetMbps).toFixed(1)} H${w}` : '', max: top };
}

export function historyStat(h: History): string {
  const pos = h.filter((p) => p.v > 0);
  if (!pos.length) return 'no data yet';
  const avg = pos.reduce((a, p) => a + p.v, 0) / pos.length;
  const min = Math.min(...h.filter((p) => p.v >= 0).map((p) => p.v));
  return `avg ${avg.toFixed(1)} · min ${min.toFixed(1)}`;
}

/** Dips: runs of warn/bad windows. */
export function dips(h: History): { count: number; lastAgoSec?: number } {
  let count = 0, inDip = false, last = -1;
  h.forEach((p, i) => {
    const bad = p.q === 'warn' || p.q === 'bad';
    if (bad && !inDip) count++;
    if (bad) last = i;
    inDip = bad;
  });
  return { count, lastAgoSec: last >= 0 ? (h.length - 1 - last) * 5 : undefined };
}

const ago = (s: number) => (s < 60 ? `${s} s ago` : `${Math.round(s / 60)} min ago`);

/** One line under the chart. */
export function summarize(h: History, state: OutputState, sinceSec?: number): { text: string; tone: Tone } {
  if (state === 'unknown') return { text: '? Media server unreachable, state unknown', tone: 'mute' };
  if (state === 'stopped') return { text: '○ Off', tone: 'mute' };
  if (state === 'stopping') return { text: '◔ Stopping now', tone: 'mute' };
  if (state === 'waiting') return { text: '○ No stream yet', tone: 'mute' };
  if (state === 'failed') return { text: '✕ Not on air', tone: 'bad' };
  if (state === 'starting') return { text: h.some((p) => p.v > 0) ? '◔ Connecting again' : '◔ No history yet', tone: 'start' };
  if (state === 'reconnecting') return { text: '▲ Dropping now', tone: 'warn' };
  const last = h[h.length - 1];
  if (last && (last.q === 'warn' || last.q === 'bad')) return { text: '▲ Unstable right now', tone: 'warn' };
  const d = dips(h);
  if (d.count) return { text: `▲ ${d.count} dip${d.count > 1 ? 's' : ''}, last ${ago(d.lastAgoSec ?? 0)} · fine now`, tone: 'warn' };
  const span = Math.min(sinceSec ?? Infinity, h.filter((p) => p.v >= 0).length * 5);
  if (!h.length || span < 5) return { text: '● Just started', tone: 'ok' };
  return { text: span >= 300 ? '● Steady for 5 min' : `● Steady for ${span < 60 ? `${span} s` : `${Math.floor(span / 60)} min`}`, tone: 'ok' };
}

export const toneClass: Record<Tone, string> = {
  ok: 'text-text-soft', warn: 'text-warn', bad: 'text-fail', mute: 'text-muted', start: 'text-start',
};

export type StateColor = 'live' | 'start' | 'warn' | 'fail' | 'idle';

/** Visual spec per card state. Border style keeps states apart without colour. */
export const STATE_SPEC: Record<OutputState, { color: StateColor; badge: string; border: 'solid' | 'dashed' | 'dotted' }> = {
  live: { color: 'live', badge: '● LIVE', border: 'solid' },
  starting: { color: 'start', badge: '◔ CONNECTING', border: 'dashed' },
  reconnecting: { color: 'warn', badge: '↻ RETRYING', border: 'dashed' },
  stopping: { color: 'idle', badge: '◔ STOPPING', border: 'dashed' },
  stopped: { color: 'idle', badge: '■ OFF', border: 'solid' },
  waiting: { color: 'idle', badge: '○ WAITING', border: 'dotted' },
  failed: { color: 'fail', badge: '✕ FAILED', border: 'solid' },
  unknown: { color: 'idle', badge: '? NO DATA', border: 'dotted' },
};
